const {registerModule, KitModule} = window;

const VIMEO_PLAYER_ORIGIN = 'https://player.vimeo.com';

class Video extends KitModule {
    constructor($element) {
        super($element);

        this.setVars();
        this.bindUI();
        this.bindEvents();
    }

    /**
   * Binds event listeners for video module
   * @listens Modal#modalclose
   * @listens Modal#modalshow
   * @see _src/_includes/elements/modal/modal.js
   */
    bindEvents() {
        this.$element.addEventListener('click', this);
        if (this.$modal) {
            this.$modal.addEventListener('modalclose', this.handleModalClose.bind(this));
            this.$modal.addEventListener('modalshow', this.handleModalShow.bind(this));
        }
    }

    bindUI() {
        this.$modal = document.getElementById(this.modalId);
    }

    /**
   * @param {HTMLIFrameElement} iframe
   * @param {string} method
   */
    postMessageToVimeo(iframe, method) {
        if (!iframe?.contentWindow) return;
        iframe.contentWindow.postMessage(
            JSON.stringify({method}),
            VIMEO_PLAYER_ORIGIN,
        );
    }

    /**
   * @param {HTMLElement} root
   * @return {HTMLIFrameElement|null}
   */
    getVimeoIframe(root) {
        const liteVimeo = root?.querySelector('lite-vimeo');
        return liteVimeo?.querySelector('iframe') || root?.querySelector('.lite-video iframe') || null;
    }

    /**
   * @param {HTMLElement} root
   */
    pauseVimeoIn(root) {
        const iframe = this.getVimeoIframe(root);
        if (iframe) {
            this.postMessageToVimeo(iframe, 'pause');
        }
    }

    /**
   * @param {HTMLElement} root
   */
    playVimeoIn(root) {
        const iframe = this.getVimeoIframe(root);
        if (iframe) {
            this.postMessageToVimeo(iframe, 'play');
        }
    }

    /**
   * @param {HTMLElement} root
   */
    pauseEmbedIn(root) {
        const liteYoutube = root?.querySelector('lite-youtube');
        if (liteYoutube?.pause) {
            liteYoutube.pause();
            return;
        }
        this.pauseVimeoIn(root);
    }

    /**
   * @param {HTMLElement} root
   */
    playEmbedIn(root) {
        const liteYoutube = root?.querySelector('lite-youtube');
        if (liteYoutube?.play) {
            liteYoutube.play();
            return;
        }
        this.playVimeoIn(root);
    }

    /**
   * @param {HTMLElement} liteEmbed
   * @return {Promise<HTMLIFrameElement|undefined>}
   */
    async activateLiteEmbed(liteEmbed) {
        if (!liteEmbed || typeof liteEmbed.activate !== 'function') {
            return undefined;
        }
        return liteEmbed.activate();
    }

    /**
   * Pause the modal video on close and resume inline preview silently
   */
    handleModalClose() {
        const htmlVideo = this.$modal.querySelector('video');
        if (htmlVideo) {
            htmlVideo.pause();
        } else {
            this.pauseEmbedIn(this.$modal);
        }

        const inlineVideo = this.$element.querySelector('video');
        if (inlineVideo) {
            inlineVideo.muted = true;
            inlineVideo.play().catch(() => {});
            return;
        }

        if (this.$element.querySelector('lite-vimeo, lite-youtube')) {
            this.playEmbedIn(this.$element);
        }
    }

    /**
   * Autoplay the video when modal opens
   */
    async handleModalShow() {
        const inlineVideo = this.$element.querySelector('video');
        if (inlineVideo) {
            inlineVideo.pause();
            inlineVideo.currentTime = 0;
        } else {
            this.pauseEmbedIn(this.$element);
        }

        const htmlVideo = this.$modal.querySelector('video');
        if (htmlVideo) {
            htmlVideo.currentTime = 0;
            htmlVideo.muted = false;
            htmlVideo.play().catch(() => {});
            return;
        }

        const modalLiteVimeo = this.$modal.querySelector('lite-vimeo');
        if (modalLiteVimeo) {
            const iframe = await this.activateLiteEmbed(modalLiteVimeo);
            if (iframe) {
                iframe.addEventListener('load', () => {
                    this.postMessageToVimeo(iframe, 'play');
                }, {once: true});
                this.postMessageToVimeo(iframe, 'play');
            }
            return;
        }

        const modalLiteYoutube = this.$modal.querySelector('lite-youtube');
        if (modalLiteYoutube) {
            await this.activateLiteEmbed(modalLiteYoutube);
            modalLiteYoutube.play?.();
            return;
        }

        const iframe = this.getVimeoIframe(this.$modal);
        if (iframe) {
            this.postMessageToVimeo(iframe, 'play');
        }
    }

    /**
   * The micromodalTrigger is an a element that links to the video URL so non-JS
   * users can still view the video. This disables the normal clicking behavior
   * of the <a> element.
   * @param {Event} event
   */
    onclick(event) {
        const playButton = event.target.closest('[data-js-video-play]');
        if (playButton) {
            event.preventDefault();
            const liteVideo = playButton.closest('.lite-video');
            const video = liteVideo?.querySelector('video');
            if (!video) return;

            liteVideo.classList.add('is-activated');
            video.controls = true;
            video.muted = false;
            video.autoplay = false;
            video.loop = false;
            video.play().catch(() => {});
            playButton.setAttribute('hidden', '');
            return;
        }

        if (this.modalId) {
            event.preventDefault();
        }
    }

    setVars() {
        this.modalId = this.$element.dataset.micromodalTrigger;
    }
}

registerModule('Video', Video);
